import { Close, Search } from '@mui/icons-material';
import { Box, Chip, InputAdornment, TextField } from '@mui/material';
import type React from 'react';
import { useState } from 'react';
import Button from '../../../core-components/theme/components/Button/Button';
import { FilterCategory } from '../hooks/useNotificationsFilter';
import { trackEvent } from '../../../modules/analytics/trackingMiddleware';
import { NOTIFICATION_FILTER_APPLIED } from '../../../modules/analytics/events';
import { useTranslation } from 'react-i18next';


const filtersContainerStyles = {
  py: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
};

const searchBoxStyles = {
  mb: 2,
};

const chipGroupStyles = {
  display: 'flex',
  gap: 2,
  flexWrap: 'wrap',
  alignItems: 'center',
};

const clearAllButtonStyles = {
  color: 'text.secondary',
  textTransform: 'none',
  fontSize: '0.875rem',
  minWidth: 'auto',
  p: 0.5,
};

const filterChips = [
  { id: 'high', label: 'High Priority', key: 'notifications.filters.highPriority' },
  { id: 'medium', label: 'Medium Priority', key: 'notifications.filters.mediumPriority' },
  { id: 'low', label: 'Low Priority', key: 'notifications.filters.lowPriority' },
  { id: 'escalation', label: 'Escalation', key: 'notifications.filters.escalation' },
  { id: 'read', label: 'Read', key: 'notifications.filters.read' },
  { id: 'unread', label: 'Unread', key: 'notifications.filters.unread' },
];

interface NotificationFiltersProps {
  searchQuery: string;
  selectedFilters: string[];
  onSelect: (param: FilterCategory, value: string | string[], isToggle?: boolean) => void;
}

export const NotificationFilters: React.FC<NotificationFiltersProps> = ({
  searchQuery,
  selectedFilters,
  onSelect,
}) => {
  const { t } = useTranslation();
  const [internalSearch, setInternalSearch] = useState(searchQuery);
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setInternalSearch(value);
    onSelect('search', value);
  };

  const handleFilterChipClick = (filterId: string) => {
    onSelect('filter', filterId, true);
    trackEvent(NOTIFICATION_FILTER_APPLIED, { filterId });
  };

  const handleClearAllFilters = () => {
    onSelect('filter', []);
  };

  return (
    <Box sx={filtersContainerStyles}>
      <TextField
        fullWidth
        placeholder={t('notifications.searchPlaceholder', 'Search Notification')}
        value={internalSearch}
        onChange={handleSearchChange}
        sx={searchBoxStyles}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search />
            </InputAdornment>
          ),
        }}
      />

      <Box sx={chipGroupStyles}>
        {filterChips.map(chip => (
          <Chip
            key={chip.id}
            label={t(chip.key, chip.label)}
            color="Primary"
            variant={selectedFilters.includes(chip.id) ? 'filled' : 'outlined'}
            onClick={() => handleFilterChipClick(chip.id)}
            onDelete={
              selectedFilters.includes(chip.id) ? () => handleFilterChipClick(chip.id) : undefined
            }
            deleteIcon={selectedFilters.includes(chip.id) ? <Close /> : undefined}
            clickable
          />
        ))}
        {selectedFilters.length > 0 && (
          <Button onClick={handleClearAllFilters} sx={clearAllButtonStyles} variant="text">
            {t('notifications.filters.clearAll', 'Clear all')}
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default NotificationFilters;